import { PortalTelemetryScreen } from '../../app/telemetry/portalTelemetryScreens';
import { portalAction, portalScreenContext } from '../../app/telemetry/portalTelemetry';
import { usePortalScreen } from '../../app/telemetry/usePortalTelemetry';
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import AppFileInput from "../../app/components/AppFileInput";
import { useFormContext } from "react-hook-form";
import { FileDto } from "../../app/models/membership/fileDto";
import { MemberTransferDocumentTypeEnum } from "./common/memberTransferDocumentTypeEnum";
import { FileDocument } from "./FileDocument";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import { deleteFamTransferDocumentAsync, saveFamTransferDocumentAsync }
    from "../../app/slices/famTransferSlice";
import { toast } from "react-toastify";
import { API_REQUEST_FULLFILLED, MESSAGE_FILE_DELETED_SCESSFULLY, MESSAGE_FILE_UPLOADED_SCESSFULLY, MESSAGE_PROCESSING_DETAILS, SKIP_TO_UPLOAD } from "../../app/utils/constant";
import { ApiRequestStatus } from "../../app/enums/apiRequestStatus";
import LoadingComponent from "../../app/layout/LoadingComponent";
import UnsavedChangesDialog from "../../app/components/UnsavedChangesDialog";
import { useEffect, useState } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import MembershipFormModel from "../../app/models/membership/membershipFormModel";
import { FAMOnboarding } from "../../app/models/membership/famOnboarding";
import { certificateValidationSchema, cvValidationSchema, examValidationSchema } from "./common/membershipTransferValidationSchemas";

interface Props {
    documents: FileDto[] | null;
    famTransferDetails: FAMOnboarding | null;
    unSavedCertificateChange: boolean;
    certificateChange: () => void;
    unSavedExamChange: boolean;
    examChange: () => void;
    unSavedCvChange: boolean;
    cvChange: () => void;
    documentSubmittedCallback: () => void;
}

export function AddSupportingDocument({ documents, famTransferDetails, unSavedCertificateChange,
    certificateChange, unSavedExamChange, examChange, unSavedCvChange, cvChange,
    documentSubmittedCallback }: Props) {
    const methods = useFormContext();
    const { control, getValues, setValue, watch, trigger, reset } = methods;
    const formObj = methods.watch();
    const { famTransferDocumentsStatus, saveFamTransferDocumentStatus,
        famTransferDownloadStatus, deleteFamTransferDocumentStatus } =
        useAppSelector(state => state.famTransfer);

    const {
        formField: {
            Certificate,
            Exam,
            CV
        }
    } = MembershipFormModel;

    const certificateDocument = documents?.find(x => x.tags?.FILE_TYPE === MemberTransferDocumentTypeEnum.CERTIFICATE);
    const examDocument = documents?.find(x => x.tags?.FILE_TYPE === MemberTransferDocumentTypeEnum.EXAM);
    const curriculumVitaeDocument = documents?.find(x => x.tags?.FILE_TYPE === MemberTransferDocumentTypeEnum.CV);

    const [showDialog, setShowDialog] = useState(true);

    const [showDuplicateErrorMessage, setShowDuplicateErrorMessage] = useState(false);

    const [isCertificateUploading, setIsCertificateUploading] = useState(false);
    const [isExamUploading, setIsExamUploading] = useState(false);
    const [isCvUploading, setIsCvUploading] = useState(false);

    const [deleteCertificateConfirmation, setDeleteCertificateConfirmation] = useState(false);
    const [deleteExamConfirmation, setDeleteExamConfirmation] = useState(false);
    const [deleteCvConfirmation, setDeleteCvConfirmation] = useState(false);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (documents && documents?.length > 0) {
            reset({
                [Certificate.name]: certificateDocument,
                [Exam.name]: examDocument,
                [CV.name]: curriculumVitaeDocument
            });
            trigger();
        }
    }, [documents, certificateDocument, examDocument, curriculumVitaeDocument, setValue]);

    const populateFileFormData = (famExamId: string, fileType: string, file: File) => {
        const formData = new FormData();
        formData.append("FamExamId", famExamId);
        formData.append("FileType", fileType);
        formData.append("File", file);

        return formData;
    }

    const uploadCertificateFileAsync = async () => {
        if (certificateUploadFilter() === true) {
            setShowDuplicateErrorMessage(true);
        }
        else {
            const isStepValid = certificateValidationSchema.isValidSync(formObj.Certificate as File);

            if (isStepValid === true) {
                let famExamId = famTransferDetails?.famExamId ? famTransferDetails?.famExamId : '';

                dispatch(saveFamTransferDocumentAsync(populateFileFormData(famExamId,
                    MemberTransferDocumentTypeEnum.CERTIFICATE, formObj.Certificate as File))).then((response: any) => {
                        if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                            documentSubmittedCallback();
                            setValue(Certificate.name, formObj.Certificate);
                            setIsCertificateUploading(false);
                            certificateChange();
                            toast.success(MESSAGE_FILE_UPLOADED_SCESSFULLY);

                            trigger();
                        };
                    });
            }
        }
    }

    const uploadExamFileAsync = async () => {
        if (examUploadFilter() === true) {
            setShowDuplicateErrorMessage(true);
        }
        else {
            const isStepValid = examValidationSchema.isValidSync(formObj.Exam as File);

            if (isStepValid === true) {
                let famExamId = famTransferDetails?.famExamId ? famTransferDetails?.famExamId : '';

                dispatch(saveFamTransferDocumentAsync(populateFileFormData(famExamId,
                    MemberTransferDocumentTypeEnum.EXAM, formObj.Exam as File))).then((response: any) => {
                        if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                            documentSubmittedCallback();
                            setValue(Exam.name, formObj.Exam);
                            setIsExamUploading(false);
                            examChange();
                            toast.success(MESSAGE_FILE_UPLOADED_SCESSFULLY);
                        };
                    });
            }
        }
    }

    const uploadCurriculumVitaeFileAsync = async () => {
        if (cvUploadFilter() === true) {
            setShowDuplicateErrorMessage(true);
        }
        else {

            const isStepValid = cvValidationSchema.isValidSync(formObj.CV as File);

            if (isStepValid === true) {
                let famExamId = famTransferDetails?.famExamId ? famTransferDetails?.famExamId : '';

                dispatch(saveFamTransferDocumentAsync(populateFileFormData(famExamId,
                    MemberTransferDocumentTypeEnum.CV, formObj.CV as File))).then((response: any) => {
                        if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                            documentSubmittedCallback();
                            setValue(CV.name, formObj.CV);
                            setIsCvUploading(false);
                            cvChange();
                            toast.success(MESSAGE_FILE_UPLOADED_SCESSFULLY);
                        };
                    });
            }
        }
    }

    const certificateUploadFilter = () => {
        return getValues([Certificate.name])[0] && documents && (documents?.filter(x => x.name?.trim() ===
            (getValues([Certificate.name])[0]).name.trim()).length > 0);
    }

    const examUploadFilter = () => {
        return getValues([Exam.name])[0] && documents && (documents?.filter(x => x.name?.trim() ===
            (getValues([Exam.name])[0]).name.trim()).length > 0);
    }

    const cvUploadFilter = () => {
        return getValues([CV.name])[0] && documents && (documents?.filter(x => x.name?.trim() ===
            (getValues([CV.name])[0]).name.trim()).length > 0);
    }

    useEffect(() => {
        watch((value, { name, type }) => {
            setShowDialog(true);
            if (name === MembershipFormModel.formField.Certificate.name && type === 'change') {
                setIsCertificateUploading(true);
            }

            if (name === MembershipFormModel.formField.Exam.name && type === 'change') {
                setIsExamUploading(true);
            }

            if (name === MembershipFormModel.formField.CV.name && type === 'change') {
                setIsCvUploading(true);
            }
        });
    }, [watch]);

    const noButtonClick = () => {
        setShowDialog(false);
        setShowDuplicateErrorMessage(false);

        if (isCertificateUploading === true) {
            setValue(Certificate.name, undefined);
        }

        if (isExamUploading === true) {
            setValue(Exam.name, undefined);
        }

        if (isCvUploading === true) {
            setValue(CV.name, undefined);
        }
    }

    const deleteCertificateAsync = () => {
        if (certificateDocument?.tags?.FILE_TYPE) {
            dispatch(deleteFamTransferDocumentAsync(certificateDocument?.tags?.FILE_TYPE)).then((response: any) => {
                if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                    documentSubmittedCallback();
                    setValue(Certificate.name, undefined);
                    toast.success(MESSAGE_FILE_DELETED_SCESSFULLY);
                };
            });
        }
    }

    const deleteExamAsync = () => {
        if (examDocument?.tags?.FILE_TYPE) {
            dispatch(deleteFamTransferDocumentAsync(examDocument?.tags?.FILE_TYPE)).then((response: any) => {
                if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                    documentSubmittedCallback();
                    setValue(Exam.name, undefined);
                    toast.success(MESSAGE_FILE_DELETED_SCESSFULLY);
                };
            });
        }
    }

    const deleteCurriculumVitaeAsync = () => {
        if (curriculumVitaeDocument?.tags?.FILE_TYPE) {
            dispatch(deleteFamTransferDocumentAsync(curriculumVitaeDocument?.tags?.FILE_TYPE)).then((response: any) => {
                if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                    documentSubmittedCallback();
                    setValue(CV.name, undefined);
                    toast.success(MESSAGE_FILE_DELETED_SCESSFULLY);
                };
            });
        }
    }

    usePortalScreen(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), undefined, !((famTransferDocumentsStatus === ApiRequestStatus.Pending || famTransferDownloadStatus === ApiRequestStatus.Pending ||
        deleteFamTransferDocumentStatus === ApiRequestStatus.Pending || saveFamTransferDocumentStatus === ApiRequestStatus.Pending)));

        if (famTransferDocumentsStatus === ApiRequestStatus.Pending || famTransferDownloadStatus === ApiRequestStatus.Pending ||
        deleteFamTransferDocumentStatus === ApiRequestStatus.Pending || saveFamTransferDocumentStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <>
        <Box sx={{ minWidth: "100%" }} component={Paper}>
            <Grid container rowSpacing={4} sx={{ margin: "2%" }}>

                <Grid size={{ xs: 12 }}></Grid>

                <Grid size={{ xs: 12 }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: "500" }}>{Certificate.label}</Typography>
                </Grid>

                <Grid size={{ xs: 8 }}>
                    <AppFileInput control={control} label={""} name={Certificate.name} />
                </Grid>

                <Grid size={{ xs: 4 }} sx={{ marginTop: '8px' }}>
                    {isCertificateUploading === true && getValues([Certificate.name])[0] !== undefined &&
                        <Button variant="contained" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.onClick.uploadCertificateFileAsync`, uploadCertificateFileAsync)}>
                            {certificateDocument ? 'Replace certificate' : 'Upload certificate'}
                        </Button>}
                </Grid>

                {certificateDocument &&
                    <>
                        <FileDocument fileDocument={certificateDocument} />

                        <Grid size={{ xs: 12 }}>
                            <Button variant="outlined" startIcon={<DeleteIcon />} sx={{ "& .MuiButton-startIcon": { marginRight: "0px" }, marginLeft: '0' }}
                                onClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.onClick.setDeleteCertificateConfirmation`, () => certificateDocument?.name && setDeleteCertificateConfirmation(true))} >
                                Delete
                            </Button>
                        </Grid>
                    </>
                }
                <Grid size={{ xs: 12 }}></Grid>

            </Grid>
        </Box>

        <Box sx={{ minWidth: "100%" }} component={Paper}>
            <Grid container rowSpacing={4} sx={{ margin: "2%" }}>

                <Grid size={{ xs: 12 }}></Grid>
                
                <Grid size={{ xs: 12 }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: "500" }}>{Exam.label}</Typography>
                </Grid>

                <Grid size={{ xs: 8 }}>
                    <AppFileInput control={control} label={""} name={Exam.name} />
                </Grid>

                <Grid size={{ xs: 4 }} sx={{ marginTop: '8px' }}>
                    {isExamUploading === true && getValues([Exam.name])[0] !== undefined &&
                        <Button variant="contained" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.onClick.uploadExamFileAsync`, uploadExamFileAsync)}>
                            {examDocument ? 'Replace file' : 'Upload file'}
                        </Button>}
                </Grid>

                {examDocument &&
                    <>
                        <FileDocument fileDocument={examDocument} />

                        <Grid size={{ xs: 2 }}>
                            <Button sx={{ "& .MuiButton-startIcon": { marginRight: "0px", marginLeft: '0' } }} variant="outlined" startIcon={<DeleteIcon />}
                                onClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.onClick.setDeleteExamConfirmation`, () => examDocument?.name && setDeleteExamConfirmation(true))} >
                                Delete
                            </Button>
                        </Grid>
                    </>
                }

                <Grid size={{ xs: 12 }}></Grid>

            </Grid>
        </Box>

        <Box sx={{ minWidth: "100%" }} component={Paper}>
            <Grid container rowSpacing={4} sx={{ margin: "2%" }}>

                <Grid size={{ xs: 12 }}></Grid>
                
                <Grid size={{ xs: 12 }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: "500" }}>{CV.label}</Typography>
                </Grid>

                <Grid size={{ xs: 8 }}>
                    <AppFileInput control={control} label={""} name={CV.name} />
                </Grid>

                <Grid size={{ xs: 4 }} sx={{ marginTop: '8px' }}>
                    {isCvUploading === true && getValues([CV.name])[0] !== undefined &&
                        <Button variant="contained" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.onClick.uploadCurriculumVitaeFileAsync`, uploadCurriculumVitaeFileAsync)}>
                            {curriculumVitaeDocument ? 'Replace curriculum vitae' : 'Upload curriculum vitae'}
                        </Button>}
                </Grid>

                {curriculumVitaeDocument &&
                    <>
                        <FileDocument fileDocument={curriculumVitaeDocument} />

                        <Grid size={{ xs: 2 }}>
                            <Button variant="outlined" startIcon={<DeleteIcon />} sx={{ "& .MuiButton-startIcon": { marginRight: "0px" } }}
                                onClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.onClick.setDeleteCvConfirmation`, () => curriculumVitaeDocument?.name && setDeleteCvConfirmation(true))} >
                                Delete
                            </Button>
                        </Grid>
                    </>
                }

                <Grid size={{ xs: 12 }}></Grid>

            </Grid>
        </Box>

        {showDuplicateErrorMessage === true &&
            <UnsavedChangesDialog showDialog={showDialog}
                title={'Duplicate file found.'}
                subTitle={`File you uploaded is already in the system. You can either 
                    replace this file or rename the file and upload again.`}
                skipButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.skipButtonClick.noButtonClick`, noButtonClick)}
                showYesNoButtons={false} />
        }

        {unSavedCertificateChange === true &&
            <UnsavedChangesDialog showDialog={showDialog}
                title={'Changes not saved.'}
                subTitle={`Certificate is not saved, do you wan to save the changes?`}
                yesButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.yesButtonClick.uploadCertificateFileAsync`, uploadCertificateFileAsync)}
                noButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.noButtonClick.setValue`, () => {
                    setValue(Certificate.name, SKIP_TO_UPLOAD);
                    setIsCertificateUploading(false);
                    certificateChange();
                    trigger();
                })}
                showYesNoButtons={true} />
        }

        {unSavedExamChange === true &&
            <UnsavedChangesDialog showDialog={showDialog}
                title={'Changes not saved.'}
                subTitle={`Exam document is not saved, do you wan to save the changes?`}
                yesButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.yesButtonClick.uploadExamFileAsync`, uploadExamFileAsync)}
                noButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.noButtonClick.setValue`, () => {
                    setValue(Exam.name, SKIP_TO_UPLOAD);
                    setIsExamUploading(false);
                    examChange();
                    trigger();
                })}
                showYesNoButtons={true} />
        }

        {unSavedCvChange === true &&
            <UnsavedChangesDialog showDialog={showDialog}
                title={'Changes not saved.'}
                subTitle={`CV is not saved, do you wan to save the changes?`}
                yesButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.yesButtonClick.uploadCurriculumVitaeFileAsync`, uploadCurriculumVitaeFileAsync)}
                noButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.noButtonClick.setValue`, () => {
                    setValue(CV.name, SKIP_TO_UPLOAD);
                    setIsCvUploading(false);
                    cvChange();
                    trigger();
                })}
                showYesNoButtons={true} />
        }

        {deleteCertificateConfirmation &&
            <UnsavedChangesDialog showDialog={showDialog}
                title={'Are you sure you want to delete Certificate?'}
                subTitle={`You will loose the changes on clicking "YES".`}
                yesButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.yesButtonClick.deleteCertificateAsync`, () => {
                    deleteCertificateAsync();
                    setDeleteCertificateConfirmation(false);
                })}
                noButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.noButtonClick.setDeleteCertificateConfirmation`, () => setDeleteCertificateConfirmation(false))}
                showYesNoButtons={true} />}

        {deleteExamConfirmation &&
            <UnsavedChangesDialog showDialog={showDialog}
                title={'Are you sure you want to delete Exam document?'}
                subTitle={`You will loose the changes on clicking "YES".`}
                yesButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.yesButtonClick.deleteExamAsync`, () => {
                    deleteExamAsync();
                    setDeleteExamConfirmation(false);
                })}
                noButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.noButtonClick.setDeleteExamConfirmation`, () => setDeleteExamConfirmation(false))}
                showYesNoButtons={true} />}

        {deleteCvConfirmation &&
            <UnsavedChangesDialog showDialog={showDialog}
                title={'Are you sure you want to delete CV?'}
                subTitle={`You will loose the changes on clicking "YES".`}
                yesButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.yesButtonClick.deleteCurriculumVitaeAsync`, () => {
                    deleteCurriculumVitaeAsync();
                    setDeleteCvConfirmation(false);
                })}
                noButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.AddSupportingDocument), `${PortalTelemetryScreen.AddSupportingDocument}.noButtonClick.setDeleteCvConfirmation`, () => setDeleteCvConfirmation(false))}
                showYesNoButtons={true} />}
    </>
}
